import React from "react";
import { GitMerge, Plus, Spline, Trash2, Eye, EyeOff, ScanSearch, Zap } from "lucide-react";
import OrderedIconActionContextRow from "../../ui/OrderedIconActionContextRow";
import { stripNamespacePrefix as stripPrefix } from "../../../utils/nameUtils";
import { ReparentMenuIcon } from "./icons";

export default function SceneGraphJointContextMenu({
  contextMenuJointIds,
  addNewSubmenuOpen,
  reparentSubmenuOpen,
  setJointActionSubmenuKey,
  closeContextSubmenus,
  setContextMenuOpen,
  isPhysicsRunning,
  worldRootTargetBodyUuid,
  groupedMeshParentOptions,
  bodies,
  viewerActions,
  jointVizVisibility,
  inspectContextMenuTarget,
  handleDeleteJoints,
  handleMoveJointsToBody,
  handleAddActuatorForJoint,
  handleAddConstraintForJoint,
  handleAddTendonForJoint,
  renderJointReparentBodyButton,
}) {
  return (
    <>
      <div className="px-2 py-1.5 text-[11px] font-semibold text-gray-400 uppercase tracking-wide">
        {contextMenuJointIds.length} {contextMenuJointIds.length === 1 ? "joint" : "joints"}{" "}
        selected
      </div>
      <div className="px-2 pb-1.5">
        <OrderedIconActionContextRow
          activeSubmenuKey={reparentSubmenuOpen ? "reparent" : addNewSubmenuOpen ? "addNew" : null}
          setActiveSubmenuKey={setJointActionSubmenuKey}
          submenuClassName="mt-1 min-w-[180px] rounded-md border border-gray-600 p-0.5 shadow-xl"
          submenuStyle={{ backgroundColor: "rgba(40, 40, 40, 0.95)" }}
          actions={[
            {
              key: "inspect",
              label: "Inspect",
              icon: <ScanSearch className="w-4 h-4" aria-hidden="true" />,
              onDefaultClick: inspectContextMenuTarget,
            },
            (() => {
              const allJointVisible = contextMenuJointIds.every(
                (id) => jointVizVisibility[id] !== false
              );
              return {
                key: "visibility",
                label: allJointVisible ? "Hide" : "Show",
                icon: allJointVisible ? (
                  <EyeOff className="w-4 h-4" aria-hidden="true" />
                ) : (
                  <Eye className="w-4 h-4" aria-hidden="true" />
                ),
                disabled: isPhysicsRunning,
                disabledByPhysics: isPhysicsRunning,
                onDefaultClick: () => {
                  if (isPhysicsRunning) return;
                  contextMenuJointIds.forEach((id) =>
                    viewerActions.setJointVizVisible(id, !allJointVisible)
                  );
                  setContextMenuOpen(false);
                },
              };
            })(),
            {
              key: "delete",
              label: "Delete",
              icon: <Trash2 className="w-4 h-4" aria-hidden="true" />,
              disabled: isPhysicsRunning,
              disabledByPhysics: isPhysicsRunning,
              onHover: closeContextSubmenus,
              onClick: () => handleDeleteJoints(contextMenuJointIds),
            },
            {
              key: "reparent",
              label: "Reparent",
              icon: <ReparentMenuIcon />,
              disabled: isPhysicsRunning || contextMenuJointIds.length === 0,
              disabledByPhysics: isPhysicsRunning,
              onDefaultClick: () => {
                if (
                  isPhysicsRunning ||
                  contextMenuJointIds.length === 0 ||
                  !worldRootTargetBodyUuid
                )
                  return;
                handleMoveJointsToBody(worldRootTargetBodyUuid);
              },
              submenu: (
                <div className="max-h-48 overflow-y-auto space-y-0.5">
                  {groupedMeshParentOptions
                    ? groupedMeshParentOptions.map((group) => (
                        <div key={group.label}>
                          <div className="px-2 py-1 text-[10px] font-semibold text-gray-400 uppercase tracking-wide">
                            {group.label}
                          </div>
                          {group.bodies.map(renderJointReparentBodyButton)}
                        </div>
                      ))
                    : Object.values(bodies || {})
                        .filter((body) => body?.uuid)
                        .sort((a, b) =>
                          String(stripPrefix(a.name) || "").localeCompare(
                            String(stripPrefix(b.name) || "")
                          )
                        )
                        .map(renderJointReparentBodyButton)}
                </div>
              ),
            },
            {
              key: "addNew",
              label: "Add",
              icon: <Plus className="w-4 h-4" aria-hidden="true" />,
              disabled: isPhysicsRunning || contextMenuJointIds.length !== 1,
              disabledByPhysics: isPhysicsRunning,
              submenu:
                !isPhysicsRunning && contextMenuJointIds.length === 1 ? (
                  <div className="space-y-0.5">
                    <button
                      type="button"
                      onClick={() => handleAddActuatorForJoint(contextMenuJointIds[0])}
                      className="w-full text-left flex items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-gray-200 hover:bg-gray-700/90 transition-colors"
                    >
                      <Zap className="w-4 h-4 shrink-0" aria-hidden="true" />
                      Actuator
                    </button>
                    <button
                      type="button"
                      onClick={() => handleAddConstraintForJoint(contextMenuJointIds[0])}
                      className="w-full text-left flex items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-gray-200 hover:bg-gray-700/90 transition-colors"
                    >
                      <GitMerge className="w-4 h-4 shrink-0" aria-hidden="true" />
                      Constraint
                    </button>
                    <button
                      type="button"
                      onClick={() => handleAddTendonForJoint(contextMenuJointIds[0])}
                      className="w-full text-left flex items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-gray-200 hover:bg-gray-700/90 transition-colors"
                    >
                      <Spline className="w-4 h-4 shrink-0" aria-hidden="true" />
                      Tendon
                    </button>
                  </div>
                ) : null,
            },
          ]}
        />
      </div>
    </>
  );
}
